import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import ReactTooltip from "react-tooltip";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { startDownloadMassiveModuleExcel } from "../../../app/actions/recordExcelUpdateActions";
import {
  getModuleDisplayLabel,
  isMassiveDownloadEnabledForModule,
  MASSIVE_DOWNLOAD_FORMATS
} from "../../../app/helpers/flowEngineConfigHelper";

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    maxWidth: "720px",
    width: "90%",
    maxHeight: "90vh",
    height: "auto",
    padding: 0,
    overflow: "auto"
  }
};

if (typeof document !== "undefined") {
  Modal.setAppElement("#root");
}

const FORMAT_LABEL = {
  term: ["massive-download.format.term", "Término Clave"],
  dri: ["massive-download.format.dri", "DRI"],
  prospecto: ["massive-download.format.prospecto", "Prospecto del Caso"]
};

const FORMAT_HINT = {
  term: ["massive-download.hint-term", "Formato de término clave de este módulo, con los registros asignados a su usuario."],
  dri: ["massive-download.hint-dri", "Formato DRI de este módulo, con los casos asignados a su usuario en esta bandeja."],
  prospecto: ["massive-download.hint-prospecto", "Formato Prospecto del Caso de este módulo, con los casos asignados a su usuario en esta bandeja."]
};

const FORMAT_DOWNLOAD = {
  term: ["massive-download.download-term", "Descargar términos clave"],
  dri: ["massive-download.download-dri", "Descargar DRI"],
  prospecto: ["massive-download.download-prospecto", "Descargar Prospecto del Caso"]
};

const MassiveModuleDownloadModal = ({
  isOpen,
  toggle,
  surface,
  process
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formats = MASSIVE_DOWNLOAD_FORMATS[surface] || [];
  const [matrixMode, setMatrixMode] = useState(formats[0] || "prospecto");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMatrixMode((MASSIVE_DOWNLOAD_FORMATS[surface] || [])[0] || "prospecto");
    }
  }, [isOpen, surface]);

  const handleClose = () => {
    if (!busy) {
      toggle();
    }
  };

  const download = async () => {
    setBusy(true);
    const body = await dispatch(startDownloadMassiveModuleExcel(process, surface, matrixMode));
    setBusy(false);
    if (!body.ok) {
      Swal.fire(t("titles.error"), body.msg || t("massive-download.error", "No se pudo descargar la matriz."), "error");
    }
  };

  const moduleLabel = getModuleDisplayLabel(process, t);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      style={customStyles}
      closeTimeoutMS={200}
      className="modal"
      overlayClassName="modal-fondo"
    >
      <div className="modal-header d-flex align-items-center w-100 px-3 pt-3">
        <h5 className="modal-title mb-0">{t("massive-download.title", "Descargar matriz del módulo")}</h5>
        <button type="button" className="btn-close ms-auto" aria-label="Close" onClick={handleClose}></button>
      </div>
      <div className="modal-body px-3 pb-3">
        <div className="alert alert-warning">
          {t(
            "massive-download.legend",
            "Descarga todos los registros de este módulo asignados a su usuario, una fila por registro, con el formato elegido. No mueve el flujo ni modifica los datos."
          )}
        </div>
        {formats.length > 1 && (
          <div className="mb-3">
            <div className="btn-group" role="group" aria-label={t("massive-download.tabs", "Formato de la matriz")}>
              {formats.map((format) => (
                <button
                  key={format}
                  type="button"
                  className={`btn btn-sm ${matrixMode === format ? "btn-primary" : "btn-outline-primary"}`}
                  onClick={() => setMatrixMode(format)}
                >
                  {t(FORMAT_LABEL[format][0], FORMAT_LABEL[format][1])}
                </button>
              ))}
            </div>
          </div>
        )}
        <p className="small text-muted">
          {t(FORMAT_HINT[matrixMode][0], FORMAT_HINT[matrixMode][1])}
        </p>
        <p className="small mb-3">
          {t("massive-download.module", "Módulo")}: <strong>{moduleLabel}</strong>
        </p>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          disabled={busy}
          onClick={download}
        >
          <i className="fa fa-download me-1" />
          {t(FORMAT_DOWNLOAD[matrixMode][0], FORMAT_DOWNLOAD[matrixMode][1])}
        </button>
      </div>
    </Modal>
  );
};

MassiveModuleDownloadModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  surface: PropTypes.string.isRequired,
  process: PropTypes.string.isRequired
};

export const MassiveModuleDownloadButton = ({ surface, process }) => {
  const { t } = useTranslation();
  const configuration = useSelector((state) => state.configuration);
  const [open, setOpen] = useState(false);
  const tipId = `massive-download-${surface}-${process || "none"}`;
  if (!isMassiveDownloadEnabledForModule(configuration, surface, process)) {
    return null;
  }
  return (
    <>
      <button
        type="button"
        data-tip
        data-for={tipId}
        className="table-button-excel"
        aria-label={t("massive-download.icon", "Descargar matriz del módulo filtrado")}
        onClick={() => setOpen(true)}
      >
        <i className="fa fa-file-excel fa-lg" />
      </button>
      <ReactTooltip id={tipId} place="right" effect="solid">
        {t("massive-download.icon", "Descargar matriz del módulo filtrado")}
      </ReactTooltip>
      <MassiveModuleDownloadModal
        isOpen={open}
        toggle={() => setOpen(false)}
        surface={surface}
        process={process}
      />
    </>
  );
};

MassiveModuleDownloadButton.propTypes = {
  surface: PropTypes.string.isRequired,
  process: PropTypes.string
};

export default MassiveModuleDownloadModal;
